// Real Web Audio DSP engine for SINGVI KARAOKE.
// Handles mic capture, live monitoring, real effects (EQ, compression, reverb,
// delay), and offline rendering (mix vocal + backing track) to a WAV file.

const EQ_FREQS = [60, 120, 250, 500, 1000, 2000, 4000, 8000, 16000];

export function defaultMix() {
  return {
    vocalVolume: 1.0,
    musicVolume: 0.8,
    reverb: { amount: 0.2, decay: 2.0 },
    delay: { time: 0.25, feedback: 0.25, mix: 0.0 },
    comp: { threshold: -24, ratio: 3, attack: 0.003, release: 0.25, makeup: 2 },
    eq: EQ_FREQS.map(() => 0),
    preset: "Balanced",
  };
}

function makeImpulse(ctx, seconds, decay) {
  const rate = ctx.sampleRate;
  const len = Math.max(1, Math.floor(rate * seconds));
  const impulse = ctx.createBuffer(2, len, rate);
  for (let c = 0; c < 2; c++) {
    const data = impulse.getChannelData(c);
    for (let i = 0; i < len; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return impulse;
}

// Build the vocal effect chain. Returns {input, output} nodes.
function buildVocalChain(ctx, mix) {
  const input = ctx.createGain();
  let node = input;

  // EQ bands
  mix.eq.forEach((gain, i) => {
    const f = ctx.createBiquadFilter();
    f.type = i === 0 ? "lowshelf" : i === EQ_FREQS.length - 1 ? "highshelf" : "peaking";
    f.frequency.value = EQ_FREQS[i];
    f.Q.value = 1.0;
    f.gain.value = gain;
    node.connect(f);
    node = f;
  });

  // Compressor
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.value = mix.comp.threshold;
  comp.ratio.value = mix.comp.ratio;
  comp.attack.value = mix.comp.attack;
  comp.release.value = mix.comp.release;
  node.connect(comp);
  node = comp;

  const makeup = ctx.createGain();
  makeup.gain.value = Math.pow(10, (mix.comp.makeup || 0) / 20);
  node.connect(makeup);
  node = makeup;

  // Parallel: dry + reverb + delay -> output
  const output = ctx.createGain();
  node.connect(output); // dry

  // Reverb
  const conv = ctx.createConvolver();
  conv.buffer = makeImpulse(ctx, mix.reverb.decay || 2, 2.5);
  const revGain = ctx.createGain();
  revGain.gain.value = mix.reverb.amount || 0;
  node.connect(conv);
  conv.connect(revGain);
  revGain.connect(output);

  // Delay
  const delay = ctx.createDelay(5.0);
  delay.delayTime.value = mix.delay.time || 0.25;
  const fb = ctx.createGain();
  fb.gain.value = mix.delay.feedback || 0;
  const delayMix = ctx.createGain();
  delayMix.gain.value = mix.delay.mix || 0;
  node.connect(delay);
  delay.connect(fb);
  fb.connect(delay);
  delay.connect(delayMix);
  delayMix.connect(output);

  return { input, output, nodes: { comp, makeup, revGain, delay, fb, delayMix, conv } };
}

export class Recorder {
  constructor() {
    this.stream = null;
    this.ctx = null;
    this.analyser = null;
    this.recorder = null;
    this.chunks = [];
    this.monitorGain = null;
  }

  async init(monitoring = false) {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
      video: false,
    });
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    const src = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    src.connect(this.analyser);
    this.monitorGain = this.ctx.createGain();
    this.monitorGain.gain.value = monitoring ? 0.8 : 0;
    src.connect(this.monitorGain);
    this.monitorGain.connect(this.ctx.destination);
    return this.listDevices();
  }

  async listDevices() {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      return devs.filter((d) => d.kind === "audioinput");
    } catch {
      return [];
    }
  }

  setMonitoring(on) {
    if (this.monitorGain) this.monitorGain.gain.value = on ? 0.8 : 0;
  }

  getLevel() {
    if (!this.analyser) return 0;
    const buf = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.min(1, Math.sqrt(sum / buf.length) * 3);
  }

  getWaveform(bars = 48) {
    if (!this.analyser) return new Array(bars).fill(0);
    const buf = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(buf);
    const step = Math.floor(buf.length / bars);
    const out = [];
    for (let i = 0; i < bars; i++) out.push((buf[i * step] || 0) / 255);
    return out;
  }

  start() {
    this.chunks = [];
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    this.recorder = new MediaRecorder(this.stream, { mimeType: mime });
    this.recorder.ondataavailable = (e) => e.data.size && this.chunks.push(e.data);
    this.recorder.start();
  }

  pause() { this.recorder?.state === "recording" && this.recorder.pause(); }
  resume() { this.recorder?.state === "paused" && this.recorder.resume(); }

  stop() {
    return new Promise((resolve) => {
      if (!this.recorder) return resolve(null);
      this.recorder.onstop = () => resolve(new Blob(this.chunks, { type: "audio/webm" }));
      this.recorder.stop();
    });
  }

  destroy() {
    try { this.stream?.getTracks().forEach((t) => t.stop()); } catch {}
    try { this.ctx?.close(); } catch {}
  }
}

async function decodeUrl(ctx, url) {
  const res = await fetch(url, { credentials: "include" });
  const arr = await res.arrayBuffer();
  return await ctx.decodeAudioData(arr);
}

async function decodeBlob(ctx, blob) {
  const arr = await blob.arrayBuffer();
  return await ctx.decodeAudioData(arr);
}

// Live preview player: plays vocal + music through real effect nodes.
export class MixPlayer {
  constructor() {
    this.ctx = null;
    this.vocalBuffer = null;
    this.musicBuffer = null;
    this.playing = false;
    this.startTime = 0;
    this.offset = 0;
  }

  async load(vocalBlob, musicUrl) {
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.vocalBuffer = await decodeBlob(this.ctx, vocalBlob);
    if (musicUrl) {
      try { this.musicBuffer = await decodeUrl(this.ctx, musicUrl); } catch { this.musicBuffer = null; }
    }
  }

  get duration() {
    return Math.max(this.vocalBuffer?.duration || 0, this.musicBuffer?.duration || 0);
  }

  play(mix, onEnded) {
    if (!this.ctx) return;
    this.stop();
    const ctx = this.ctx;
    const master = ctx.createGain();
    master.connect(ctx.destination);

    const chain = buildVocalChain(ctx, mix);
    const vgain = ctx.createGain();
    vgain.gain.value = mix.vocalVolume;
    const vsrc = ctx.createBufferSource();
    vsrc.buffer = this.vocalBuffer;
    vsrc.connect(chain.input);
    chain.output.connect(vgain);
    vgain.connect(master);

    if (this.musicBuffer) {
      const mgain = ctx.createGain();
      mgain.gain.value = mix.musicVolume;
      const msrc = ctx.createBufferSource();
      msrc.buffer = this.musicBuffer;
      msrc.connect(mgain);
      mgain.connect(master);
      msrc.start();
      this._msrc = msrc;
    }
    vsrc.start();
    vsrc.onended = () => { this.playing = false; onEnded && onEnded(); };
    this._vsrc = vsrc;
    this.playing = true;
    ctx.resume();
  }

  stop() {
    try { this._vsrc?.stop(); } catch {}
    try { this._msrc?.stop(); } catch {}
    this.playing = false;
  }

  destroy() { this.stop(); try { this.ctx?.close(); } catch {} }
}

// Offline render: mix vocal + music with effects -> WAV Blob.
export async function renderMix(vocalBlob, musicUrl, mix, onProgress) {
  const tmp = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(2, 44100, 44100);
  const vocalBuffer = await decodeBlob(tmp, vocalBlob);
  let musicBuffer = null;
  if (musicUrl) {
    try {
      const res = await fetch(musicUrl, { credentials: "include" });
      const arr = await res.arrayBuffer();
      musicBuffer = await tmp.decodeAudioData(arr);
    } catch { musicBuffer = null; }
  }
  onProgress && onProgress(20);

  const dur = Math.max(vocalBuffer.duration, musicBuffer?.duration || 0) + 0.5;
  const rate = 44100;
  const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(2, Math.ceil(dur * rate), rate);
  const master = ctx.createGain();
  master.connect(ctx.destination);

  const chain = buildVocalChain(ctx, mix);
  const vgain = ctx.createGain();
  vgain.gain.value = mix.vocalVolume;
  const vsrc = ctx.createBufferSource();
  vsrc.buffer = vocalBuffer;
  vsrc.connect(chain.input);
  chain.output.connect(vgain);
  vgain.connect(master);
  vsrc.start(0);

  if (musicBuffer) {
    const mgain = ctx.createGain();
    mgain.gain.value = mix.musicVolume;
    const msrc = ctx.createBufferSource();
    msrc.buffer = musicBuffer;
    msrc.connect(mgain);
    mgain.connect(master);
    msrc.start(0);
  }
  onProgress && onProgress(45);
  const rendered = await ctx.startRendering();
  onProgress && onProgress(80);
  const wav = bufferToWav(rendered);
  onProgress && onProgress(100);
  return { blob: new Blob([wav], { type: "audio/wav" }), duration: rendered.duration };
}

function bufferToWav(buffer) {
  const numCh = buffer.numberOfChannels;
  const len = buffer.length * numCh * 2 + 44;
  const ab = new ArrayBuffer(len);
  const view = new DataView(ab);
  const chans = [];
  let offset = 0;
  const write = (s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i)); };
  const u32 = (v) => { view.setUint32(offset, v, true); offset += 4; };
  const u16 = (v) => { view.setUint16(offset, v, true); offset += 2; };
  write("RIFF"); u32(len - 8); write("WAVE"); write("fmt ");
  u32(16); u16(1); u16(numCh); u32(buffer.sampleRate);
  u32(buffer.sampleRate * numCh * 2); u16(numCh * 2); u16(16);
  write("data"); u32(len - offset - 4);
  for (let c = 0; c < numCh; c++) chans.push(buffer.getChannelData(c));
  let pos = offset;
  for (let i = 0; i < buffer.length; i++) {
    for (let c = 0; c < numCh; c++) {
      let s = Math.max(-1, Math.min(1, chans[c][i]));
      view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      pos += 2;
    }
  }
  return ab;
}

export const EQ_LABELS = EQ_FREQS;

export const MIX_PRESETS = {
  Balanced: { vocalVolume: 1.0, musicVolume: 0.8, reverb: { amount: 0.2, decay: 2 } },
  "Vocal Forward": { vocalVolume: 1.2, musicVolume: 0.55, reverb: { amount: 0.15, decay: 1.6 } },
  "Music Forward": { vocalVolume: 0.85, musicVolume: 1.0, reverb: { amount: 0.2, decay: 2 } },
  Studio: { vocalVolume: 1.05, musicVolume: 0.75, reverb: { amount: 0.28, decay: 2.4 } },
  Warm: { vocalVolume: 1.0, musicVolume: 0.8, reverb: { amount: 0.35, decay: 3 } },
  Powerful: { vocalVolume: 1.15, musicVolume: 0.7, reverb: { amount: 0.25, decay: 2.2 } },
};

export const REVERB_PRESETS = {
  "Small Room": { amount: 0.15, decay: 1.0 },
  Studio: { amount: 0.25, decay: 2.0 },
  Hall: { amount: 0.4, decay: 3.2 },
  Arena: { amount: 0.5, decay: 4.5 },
  Plate: { amount: 0.35, decay: 2.6 },
  Cathedral: { amount: 0.6, decay: 6 },
};

export const EQ_PRESETS = {
  Natural: [0, 0, 0, 0, 0, 0, 0, 0, 0],
  Clear: [-2, -1, 0, 1, 2, 3, 3, 2, 1],
  Bright: [-3, -2, -1, 0, 1, 2, 4, 5, 4],
  Warm: [3, 3, 2, 1, 0, -1, -2, -2, -1],
  Deep: [5, 4, 2, 0, -1, -1, 0, 0, 0],
  "Voice Boost": [-2, 0, 1, 3, 4, 3, 2, 1, 0],
};
